import React, { useMemo, useState } from 'react';
import {
    Table,
    TableRow,
    TableBody,
    Box,
    useTheme,
    TableContainer,
    Tooltip,
    Divider,
    Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mdiEmail } from '@mdi/js';

import {
    IBasicUserFragment,
    IPortfolioItemMatrixListItemFragment,
} from 'graphql/types';
import { TableCell, TableHead, useSelection } from 'common/components/Table';
import { Typography } from 'common/components/Typography';
import { BoxLoader } from 'common/components/Loader';
import { Pagination } from 'common/components/Pagination';
import { InlineStickyWrapper } from 'common/components/InlineStickyWrapper';
import { FilterBar } from 'common/components/FilterBar';
import { useSkillUserList } from 'skill/hooks/useSkillUserList';
import { IFilterFormItem, IRow } from 'common/types';
import { useSkillList } from 'skill/hooks/useSkillList';
import { useSkillReportList } from 'skill/hooks/useSkillReportList';
import { Checkbox } from 'common/components/Checkbox';
import { SkillReportMatrixRow } from 'skill/components/SkillReportMatrix';
import { IconButton } from 'common/components/IconButton';
import { UserEmailDialog } from 'user/components/UserEmailDialog';

export const SkillReportMatrix = () => {
    const [translate] = useTranslation();
    const [userEmailDialogOpen, setUserEmailDialogOpen] = useState(false);

    const {
        skills,
        loading: loadingSkills,
        filters: skillFilters = [],
    } = useSkillList();

    const theme = useTheme();
    // Calculate the total height of the navigation bar + context action bar
    const navHeights = (theme.mixins.toolbar.height as number) || 0;

    const {
        users,
        loading: loadingUsers,
        paginationSettings,
        filters: userFilters,
        filterValues,
        onSearch,
        onFilterChange: onUserFilterChange,
        setPage,
    } = useSkillUserList();

    const { ...selectionProps } = useSelection(users);
    const { rows: userRows, selected, onSelectAll } = selectionProps;

    // Skill filter for report
    const skillFilterIds = useMemo(
        () => skills.map((skill) => skill.id),
        [skills]
    );

    const userIds = users.map((user) => user.id);

    const {
        items: skillReportItems,
        loading: loadingReports,
        refetch,
    } = useSkillReportList({
        queryVariables: { userIds, skillIds: skillFilterIds },
        skip: !userIds.length,
    });

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    const loading = loadingReports || loadingSkills || loadingUsers;

    const stickyOffsetLeft = {
        xs: 'auto',
        sm: theme.spacing(3),
    };

    const skillReportRows = useMemo(() => {
        const rows = new Map<
            string,
            {
                row: IRow;
                user: IBasicUserFragment;
                portfolioItems: IPortfolioItemMatrixListItemFragment[];
            }
        >();

        users.forEach((user, index) => {
            rows.set(user.id, {
                row: userRows[index],
                user: user,
                portfolioItems: [
                    ...skillReportItems.filter(
                        (item) => item.user?.id === user.id
                    ),
                ],
            });
        });

        return rows;
    }, [skillReportItems, userRows, users]);

    const filters = [...skillFilters, ...userFilters] as IFilterFormItem[];
    const skillReportRowList = Array.from(skillReportRows.values());
    const hasSkillReport = !!skills.length && !!skillReportRowList.length;

    const tableActions = (
        <Box>
            <Tooltip title={translate('skillsMatrix.mailButtonText')}>
                <Box>
                    <IconButton
                        color="inherit"
                        iconPath={mdiEmail}
                        iconSize="2.4rem"
                        onClick={() => {
                            setUserEmailDialogOpen(true);
                        }}
                    />
                </Box>
            </Tooltip>
        </Box>
    );

    const isAllSelected = useMemo(
        () => selected.length === users.length,
        [selected, users]
    );

    const hasSelection = useMemo(() => selected.length > 0, [selected]);

    return (
        <>
            <FilterBar
                useFilterDrawer
                filterDrawerProps={{
                    title: translate('reportPage.filterFormSkill.title'),
                    description: translate(
                        'reportPage.filterFormSkill.description'
                    ),
                    formProps: {
                        onReset: (values) => {
                            setPage(1);
                            onUserFilterChange(values);
                        },
                    },
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onFiltersChange={(values) => {
                    setPage(1);
                    onUserFilterChange(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !hasSkillReport && (
                <Box sx={{ mt: 8, textAlign: 'center' }}>
                    <Typography>{translate('noSkillsReports')}</Typography>
                </Box>
            )}

            {!loading && hasSkillReport && (
                <>
                    <TableContainer sx={{ overflowX: 'initial', mt: 4 }}>
                        <Table stickyHeader>
                            {!!skills.length && (
                                <TableHead>
                                    <TableRow sx={{ verticalAlign: 'bottom' }}>
                                        <TableCell
                                            padding="checkbox"
                                            sx={{
                                                top: navHeights,
                                                zIndex: 5,
                                                position: 'sticky',
                                            }}
                                        >
                                            <Checkbox
                                                checked={isAllSelected}
                                                indeterminate={
                                                    !isAllSelected &&
                                                    hasSelection
                                                }
                                                onChange={onSelectAll}
                                            />
                                        </TableCell>

                                        <TableCell
                                            colSpan={0}
                                            sx={{
                                                top: navHeights,
                                                zIndex: 5,
                                                position: 'sticky',
                                                left: stickyOffsetLeft,
                                            }}
                                        >
                                            {!hasSelection && (
                                                <Box
                                                    sx={{
                                                        pb: '3px', // Align with checkbox
                                                    }}
                                                >
                                                    {translate('participant')}
                                                </Box>
                                            )}

                                            {hasSelection && (
                                                <Stack
                                                    useFlexGap
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{
                                                        alignItems: 'center',
                                                        pb: '1px', // Align with checkbox
                                                    }}
                                                >
                                                    <Typography
                                                        fontWeight={700}
                                                        variant="body2"
                                                    >
                                                        {translate(
                                                            'selectedCount',
                                                            {
                                                                value: selected.length,
                                                            }
                                                        )}
                                                    </Typography>

                                                    <Divider
                                                        flexItem
                                                        orientation="vertical"
                                                        sx={{ ml: 1 }}
                                                    />

                                                    <Box
                                                        sx={{
                                                            height: '100%',
                                                        }}
                                                    >
                                                        {tableActions}
                                                    </Box>
                                                </Stack>
                                            )}
                                        </TableCell>

                                        {skills.map((skill) => (
                                            <React.Fragment key={skill.id}>
                                                <TableCell
                                                    vertical
                                                    sx={[
                                                        {
                                                            top: navHeights,
                                                            zIndex: 4,
                                                            left: stickyOffsetLeft,
                                                        },
                                                    ]}
                                                >
                                                    <Tooltip title={skill.name}>
                                                        <Box>
                                                            {skill.shortName ||
                                                                skill.name.substring(
                                                                    0,
                                                                    12
                                                                )}
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                            </React.Fragment>
                                        ))}
                                    </TableRow>
                                </TableHead>
                            )}

                            {!loadingReports && (
                                <TableBody>
                                    {skillReportRowList.map(
                                        ({ row, user, portfolioItems }) => {
                                            const { onSelect, isSelected } =
                                                row;

                                            return (
                                                <SkillReportMatrixRow
                                                    isSelected={isSelected}
                                                    key={user.id}
                                                    portfolioItems={
                                                        portfolioItems
                                                    }
                                                    skills={skills}
                                                    user={user}
                                                    onSelect={onSelect}
                                                    onUpdate={() => {
                                                        refetch();

                                                        if (!!selected.length) {
                                                            onSelectAll();
                                                        }
                                                    }}
                                                />
                                            );
                                        }
                                    )}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>

                    <UserEmailDialog
                        open={userEmailDialogOpen}
                        userIds={selected}
                        onClose={() => setUserEmailDialogOpen(false)}
                        onComplete={() => {
                            setUserEmailDialogOpen(false);

                            onSelectAll();
                        }}
                    />
                </>
            )}

            {loading && <BoxLoader />}

            {!loading && !!paginationSettings && hasSkillReport && (
                <Box sx={{ mt: 4 }}>
                    <InlineStickyWrapper fullWidth leftOffset={3}>
                        <Pagination
                            page={paginationSettings.page}
                            pageAmount={paginationSettings.pageAmount}
                            totalsAmount={paginationSettings.count}
                            totalsText={translate(
                                'reportPage.skillPagination.participants',
                                {
                                    count: paginationSettings.count,
                                }
                            )}
                            onChange={(page: number) => {
                                setPage?.(page);
                            }}
                        />
                    </InlineStickyWrapper>
                </Box>
            )}
        </>
    );
};
